import { Component as BaseComponent } from '../hybrid/component.js';

/**
 * Client Component - extends the isomorphic Component with import-path-based resolve().
 * Used for client-side chain resolution (offline/PWA).
 */
export class Component extends BaseComponent {
	static resolveViewPath = null;

	static async resolve(crate, component) {
		const comp = new Component();
		comp.crate = crate;
		comp.component = component;

		const base = `/crates/${crate}`;
		const viewPath = Component.resolveViewPath
			? Component.resolveViewPath(component, `${base}/views`)
			: null;

		await Promise.all([
			this.resolveView(comp, viewPath),
			this.resolveController(comp, `${base}/controllers/${component}.js`),
			this.resolveOfflineProvider(comp, `${base}/offline/${component}.js`),
			this.resolveFrontendHandler(comp, `/${crate}/frontend/${component}.js`),
		]);

		if (!comp.view && !comp.provider && !comp.controller) {
			throw new Error(`Component '${crate}/${component}' does not exist (no view, provider, or controller found)`);
		}

		return comp;
	}

	static async resolveView(comp, path) {
		if (!path) {
			comp.view = null;
			return;
		}
		try {
			const module = await import(path);
			if (!module.default) {
				throw new Error(`View '${comp.crate}/${comp.component}' has no default export`);
			}
			comp.view = path;
		} catch (e) {
			if (e.message.includes('has no default export')) throw e;
			comp.view = null;
		}
	}

	static async resolveController(comp, path) {
		try {
			const module = await import(path);
			const Ctrl = module.default;
			if (!Ctrl || (!Ctrl.$isController && !(Ctrl.prototype && Ctrl.prototype.constructor !== Object))) {
				throw new Error(`Controller '${comp.crate}/${comp.component}' has no valid controller class as default export`);
			}
			comp.controller = path;
		} catch (e) {
			if (e.message.includes('has no valid controller class')) throw e;
			comp.controller = null;
		}
	}

	static async resolveOfflineProvider(comp, path) {
		try {
			const module = await import(path);
			if (Object.keys(module).length === 0) {
				throw new Error(`Provider '${comp.crate}/${comp.component}' has no exports`);
			}
			comp.provider = path;
		} catch (e) {
			if (e.message.includes('has no exports')) throw e;
			comp.provider = null;
		}
	}

	static async resolveFrontendHandler(comp, path) {
		try {
			await import('/crates' + path);
			comp.frontendHandler = path;
		} catch {
			comp.frontendHandler = null;
		}
	}
}

export default Component;
